import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AudioRecorderData } from '@app/app/shared/models/audio-recorder/audio-recorder.model';
import * as lamejs from '@breezystack/lamejs';

const SUPPORTED_AUDIO_MIME_TYPES = [
  'audio/wav',
  'audio/webm;codecs=opus',
  'audio/webm;codecs=vorbis',
  'audio/ogg;codecs=opus',
  'audio/ogg;codecs=vorbis',
  'audio/mpeg', // mp3
  'audio/aac',
  'audio/mp4', // m4a
  'audio/flac',
];

@Component({
  selector: 'ptl-audio-recorder',
  templateUrl: './audio-recorder.component.html',
  styleUrls: ['./audio-recorder.component.scss'],
})
export class AudioRecorderComponent implements OnDestroy {
  mediaRecorder: MediaRecorder;
  audioBlobs: Blob[] = [];
  stream: MediaStream;
  recording = false;

  mp3Blob: Blob | null = null;
  mp3BlobUrl: string | null = null;
  safeMp3Url: SafeUrl | null = null;

  @Output() recordFinished = new EventEmitter<AudioRecorderData>();
  @Output() recordProcessActive = new EventEmitter<boolean>();

  constructor(private sanitizer: DomSanitizer) {}

  ngOnDestroy() {
    if (this.mp3BlobUrl) {
      URL.revokeObjectURL(this.mp3BlobUrl);
    }

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
    }
    this.recordProcessActive.emit(false);
  }

  async startRecording() {
    this.recordProcessActive.emit(true);
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.audioBlobs = [];

    const mimeType = this.getSupportedAudioMimeType();
    this.mediaRecorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.mediaRecorder.ondataavailable = (e) => this.audioBlobs.push(e.data);
    this.mediaRecorder.start();
    this.recording = true;
  }

  toggleRecording() {
    if (this.recording) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  async stopRecording() {
    this.recordProcessActive.emit(false);
    await new Promise((resolve) => {
      this.mediaRecorder.addEventListener('stop', () => resolve(null));
      this.mediaRecorder.stop();
    });

    this.stream.getTracks().forEach((t) => t.stop());
    this.recording = false;

    const mimeType = this.mediaRecorder.mimeType || 'audio/webm';
    const rawBlob = new Blob(this.audioBlobs, { type: mimeType });

    if (this.mp3BlobUrl) {
      URL.revokeObjectURL(this.mp3BlobUrl);
    }

    this.mp3Blob = await this.convertWebmToMp3(rawBlob);
    this.mp3BlobUrl = URL.createObjectURL(this.mp3Blob);
    this.safeMp3Url = this.sanitizer.bypassSecurityTrustUrl(this.mp3BlobUrl);

    this.recordFinished.emit({
      safeMp3Url: this.safeMp3Url,
      mp3BlobUrl: this.mp3BlobUrl,
      blob: this.mp3Blob,
      mimeType: mimeType,
    });
  }

  async convertWebmToMp3(blob: Blob): Promise<Blob> {
    const arrayBuffer = await blob.arrayBuffer();
    const audioCtx = new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.getChannelData(0);
    const samples = new Int16Array(channelData.length);

    channelData.forEach((v, i) => {
      samples[i] = Math.max(-1, Math.min(1, v)) * 32767;
    });

    const mp3Encoder = new lamejs.Mp3Encoder(audioBuffer.numberOfChannels, audioBuffer.sampleRate, 128);
    const mp3Chunks: Uint8Array[] = [];

    for (let i = 0; i < samples.length; i += 1152) {
      const chunk = samples.subarray(i, i + 1152);
      const buf = mp3Encoder.encodeBuffer(chunk);
      if (buf.length > 0) mp3Chunks.push(new Uint8Array(buf));
    }

    const finalChunk = mp3Encoder.flush();
    if (finalChunk.length > 0) mp3Chunks.push(new Uint8Array(finalChunk));

    return new Blob(mp3Chunks, { type: 'audio/mp3' });
  }

  private getSupportedAudioMimeType(): string {
    for (const mimeType of SUPPORTED_AUDIO_MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return '';
  }
}
